import { useEffect, useRef, useState } from 'react';

import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';

import { apiEndpoints, queryKeys, useGetQuery, usePostMutation, useUpdateMutation } from '#/services';
import { CHATBOT_ROLE } from '#/utils/constants';
import { getLocalStorageItem } from '#/utils/localStorage';

export function useChatbotMessages() {
  const contentRef = useRef(null);
  const sessionId = getLocalStorageItem('appSessionId');
  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [faqQuestionId, setFaqQuestionId] = useState('');

  const urlParams = new URLSearchParams(window.location.search);
  const chatbotId = urlParams.get('id');

  const { data: chatbotData, isFetching } = useGetQuery({
    key: queryKeys.CHATBOT_DETAIL,
    url: apiEndpoints.GET_CHATBOT_DETAILS(chatbotId),
    options: { retry: false, refetchOnWindowFocus: false, enabled: !!chatbotId },
  });

  const { data: reactions } = useGetQuery({
    key: queryKeys.REACTIONS,
    url: apiEndpoints.REACTIONS,
    options: { retry: false, refetchOnWindowFocus: false, enabled: !!chatbotId },
  });

  const { data: conversationHistory, refetch } = useGetQuery({
    key: queryKeys.CONVERSATION_HISTORY_KEY,
    url: `${apiEndpoints.GET_CONVERSATION_HISTORY}${sessionId}/`,
    params: { uuid: chatbotId },
    options: { retry: false, refetchOnWindowFocus: false, enabled: !!chatbotId },
  });

  const onMessageSendSuccess = ({ data: { results } = {} }) => {
    setMessages(results);
    setIsSending(false);
    refetch();
    setTimeout(() => {
      contentRef.current?.scrollTo({ top: contentRef.current.scrollHeight });
    }, 1);
  };

  const onMessageSendError = ({ response: { data } = {} }) => {
    toast.error(data?.detail || data?.non_field_errors?.[0] || 'Failed to send message');
    setIsSending(false);
  };

  const { mutate: onSendMessage } = usePostMutation({
    url: `${apiEndpoints.SEND_CONVERSATION}${sessionId}/?uuid=${chatbotId}&session-id=${sessionId}`,
    onSuccess: onMessageSendSuccess,
    onError: onMessageSendError,
  });

  const onReactSuccess = () => {
    refetch();
    setConversationId(null);
  };

  const onReactError = ({ response: { data } = {} }) => {
    const { detail, non_field_errors } = data || {};
    toast.error(detail || non_field_errors?.[0] || 'Reaction failed');
  };

  const { mutate: onEmojiReact } = useUpdateMutation({
    url: apiEndpoints.REACT_REACTION(conversationId),
    onSuccess: onReactSuccess,
    onError: onReactError,
  });

  const handleReaction = (id, text, emojiId, prevReaction) => () => {
    if (emojiId === 3) {
      navigator.clipboard.writeText(text).then(() => toast.success('Message copied!'));
    } else {
      setConversationId(id);
      onEmojiReact({
        payload: { reaction: emojiId === prevReaction ? null : emojiId },
      });
    }
  };

  const updateMessages = (inputField, faqId) => {
    const newMessage = {
      text: inputField,
      created: new Date().toISOString(),
      role: CHATBOT_ROLE.USER,
    };
    setMessages(prev => [...prev, newMessage]);
    setIsSending(true);
    onSendMessage({ payload: { query: inputField } });

    if (faqId) {
      setFaqQuestionId(faqId);
    }
  };

  useEffect(() => {
    if (!isEmpty(conversationHistory?.data?.results)) {
      setMessages(conversationHistory.data.results);
    }
  }, [conversationHistory]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === CHATBOT_ROLE.USER && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [messages]);

  return {
    chatbotData,
    messages,
    isSending,
    updateMessages,
    handleReaction,
    contentRef,
    reactions,
    refetch,
    faqQuestionId,
    setFaqQuestionId,
    isFetching,
  };
}
